import { useId, useState } from 'react';
import { Link } from '@tanstack/react-router';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useLlmProfiles } from '@/features/llm-profiles';
import { AiModelsLink } from '@/features/ai-models/components/AiModelsLink';
import { ProfileSummary } from '@/features/llm-profiles/components/ProfileSummary';
import type { LlmProfile } from '@/features/llm-profiles/types';

import { useDraft } from '../builder/useDraftContext';
import {
  changeStepType,
  connect,
  deleteTransition,
  findStep,
  moveStepToPhase,
  phaseOfStep,
  renameStep,
  renamedStepId,
  setStepAssignee,
  setStepField,
  setStepId,
  transitionTargets,
} from '../builder/draftOps';
import { idErrorFor, slugify } from '../builder/identifiers';
import { resolveEffectiveImageSource } from '../builder/resolveEffectiveImageSource';
import type { BuilderSelection } from '../builder/selection';
import { UNASSIGNED_PHASE_ID } from '../builder/types';
import type { DraftStepType } from '../builder/types';
import { useFieldError } from '../builder/useFieldError';
import {
  AssigneePicker,
  useAssignablePeople,
  useIsOrgAdmin,
} from '@/features/identity';

import { CommitField } from './CommitField';
import { ImagePicker } from './ImagePicker';
import { SubworkflowPicker } from './SubworkflowPicker';

/**
 * Everything one step declares that the builder edits.
 *
 * `WorkflowStepDef` requires only `id`, `name`, and `type` — nothing else is
 * mandatory, so this panel offers fields for the rest without implying they
 * must be filled in.
 */
export function StepInspector({
  // Bound again (spec 091) after `LlmProfilePicker` stopped needing it — the
  // new step-level `ImagePicker` is workspace-scoped, unlike the org-wide
  // profile picker.
  workspaceId,
  stepId,
  onSelect,
}: {
  workspaceId: string;
  stepId: string;
  onSelect: (selection: BuilderSelection) => void;
}) {
  const { draft, edit } = useDraft();
  // Each field's refusal reaches its own control through context; what the
  // handlers need here is the other half — the way to withdraw one once the
  // edit that answers it has landed.
  const assigneeFieldId = useId();
  const { people, failed: directoryFailed } = useAssignablePeople(workspaceId);
  const { resolve: resolveName } = useFieldError('name');
  const { resolve: resolvePrompt } = useFieldError('prompt');
  const { resolve: resolveWorkflow } = useFieldError('workflow');
  const { resolve: resolveSchedulingInstructions } = useFieldError(
    'scheduling_instructions',
  );
  const step = findStep(draft, stepId);
  const phaseId = phaseOfStep(draft, stepId);
  // Held only so the control can snap back to its placeholder after a pick.
  // Declared before the early return, because a hook must be.
  const [pendingTarget, setPendingTarget] = useState('');
  const targets = transitionTargets(draft, stepId);

  if (!step) return null;

  // Identifiers freeze on publication (FR-036). This is the *only* enforcement
  // of identifier permanence anywhere in the system — the service deliberately
  // does not detect renames, because under whole-document replace a rename is
  // indistinguishable from a delete plus an add (service research D12). So a
  // writable field here would silently orphan every task standing on the step.
  const identifierLocked = draft.status === 'published';

  const idError = idErrorFor(draft, step.id);

  return (
    <div className="flex flex-col gap-5">
      <CommitField
        label="Name"
        value={step.name}
        required
        field="name"
        onCommit={(name) => {
          // The id follows the name while it still may, so the selection has to
          // follow it too — otherwise the panel loses its subject the moment
          // someone renames the step they are editing.
          const nextId = renamedStepId(draft, stepId, name);
          edit(renameStep(stepId, name));
          resolveName();
          if (nextId !== stepId) onSelect({ kind: 'step', id: nextId });
        }}
      />

      <div className="flex flex-col gap-1.5">
        <Label>Type</Label>
        <Select
          value={step.type}
          onValueChange={(next) => {
            if (next) edit(changeStepType(stepId, next as DraftStepType));
          }}
        >
          <SelectTrigger aria-label="Type">
            {/*
              Base UI renders the raw *value* unless given a label function, so
              a bare `<SelectValue />` showed `ai_step` where it should show
              "AI Step". Same below, and for the phase.
            */}
            <SelectValue>
              {(value: string) => STEP_TYPE_LABELS[value]}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ai_step">AI Step</SelectItem>
            <SelectItem value="human_gate">Human Gate</SelectItem>
            <SelectItem value="linked_workflow">Sub workflow</SelectItem>
          </SelectContent>
        </Select>
        {step.type === 'human_gate' && (
          <p className="text-muted-foreground text-xs">
            Changing this to an AI Step clears the gate mode below.
          </p>
        )}
        {step.type === 'linked_workflow' && (
          <p className="text-muted-foreground text-xs">
            Changing this away from Sub workflow clears the target workflow
            below.
          </p>
        )}
      </div>

      {/*
        Shown only for a human gate (FR-028). A mode on an AI step means
        nothing and the service refuses it, so offering the control would be
        offering a choice with no effect.

        Which is also why this control carries no error state, though the
        service does report a `mode` field: the rule that names it fires only
        when the step is *not* a human gate, and this control renders only when
        it is. The two can never coincide, so marking it here would be styling
        a branch nothing can reach. A step imported with that fault shows the
        problem in the list, which is the whole account for violations with no
        input on screen to carry them.
      */}
      {step.type === 'human_gate' && (
        <div className="flex flex-col gap-1.5">
          <Label>Gate mode</Label>
          <Select
            value={step.mode ?? 'always_human'}
            onValueChange={(next) => {
              if (next) {
                edit(
                  setStepField(
                    stepId,
                    'mode',
                    next as NonNullable<typeof step.mode>,
                  ),
                );
              }
            }}
          >
            <SelectTrigger aria-label="Gate mode">
              <SelectValue>
                {(value: string) => GATE_MODE_LABELS[value]}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="always_human">Always a person</SelectItem>
              <SelectItem value="ai_or_human">A person or an agent</SelectItem>
            </SelectContent>
          </Select>

          {/*
            Beside the mode, because both answer "how is this gate handled" and
            splitting them would make the panel read as two unrelated ideas. Only
            for a human gate, for the same reason the mode is: the service refuses
            an assignee on anything else, and the database refuses it underneath.

            Unassigned is offered explicitly rather than only reachable by
            clearing the field — routing a gate to nobody is a decision someone
            makes, not the absence of one (spec 067 FR-025).
          */}
          <Label htmlFor={assigneeFieldId} className="mt-2">
            Assignee
          </Label>
          <AssigneePicker
            id={assigneeFieldId}
            people={people}
            directoryUnavailable={directoryFailed}
            value={step.assigneeUserId}
            onChange={(userId) => {
              edit(
                setStepAssignee(
                  stepId,
                  userId,
                  people.find((person) => person.id === userId) ?? null,
                ),
              );
            }}
          />
        </div>
      )}

      {step.type === 'linked_workflow' && (
        <SubworkflowPicker
          workspaceId={workspaceId}
          value={step.workflow}
          onCommit={(workflow) => {
            edit(setStepField(stepId, 'workflow', workflow));
            resolveWorkflow();
          }}
        />
      )}

      {/*
        Whether the tasks this step creates start on their own (spec 140 US4).
        Shows the service's default, Automatically, until an author picks;
        leaving it sends nothing, so the workflow keeps that default.
      */}
      {step.type === 'linked_workflow' && (
        <div className="flex flex-col gap-1.5">
          <Label>Start created tasks</Label>
          <Select
            value={step.startChildren ?? 'auto'}
            onValueChange={(next) => {
              if (next === 'auto' || next === 'manual') {
                edit(setStepField(stepId, 'startChildren', next));
              }
            }}
          >
            <SelectTrigger aria-label="Start created tasks">
              <SelectValue>
                {(value: string) => START_CHILDREN_LABELS[value]}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Automatically</SelectItem>
              <SelectItem value="manual">Wait for a person</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">
            Automatically: the scheduler starts them, in an order it reads from
            the parent's description and comments. Wait for a person: they wait
            in Backlog, and the task's followers are told.
          </p>
        </div>
      )}

      {/*
        Guidance for the scheduler that starts this step's tasks (service spec
        183). Kept visible under "Wait for a person" — the choice can change
        back, and the text is not lost — with the hint saying it is idle.
      */}
      {step.type === 'linked_workflow' && (
        <CommitField
          label="Scheduling instructions"
          value={step.schedulingInstructions ?? ''}
          multiline
          rows={4}
          maxLength={SCHEDULING_INSTRUCTIONS_MAX}
          field="scheduling_instructions"
          onCommit={(text) => {
            edit(setStepField(stepId, 'schedulingInstructions', text));
            resolveSchedulingInstructions();
          }}
          hint={
            step.startChildren === 'manual'
              ? 'The scheduler does not run when tasks wait for a person.'
              : "Extra guidance for the scheduler, e.g. 'Run frontend tasks after the API task is done.' Optional."
          }
        />
      )}

      <div className="flex flex-col gap-1.5">
        <Label>Phase</Label>
        <Select
          value={phaseId ?? UNASSIGNED_PHASE_ID}
          onValueChange={(next) => {
            if (next) edit(moveStepToPhase(stepId, next));
          }}
        >
          <SelectTrigger aria-label="Phase">
            <SelectValue>
              {(value: string) =>
                draft.phases.find((phase) => phase.id === value)?.name ??
                'Unassigned'
              }
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {draft.phases
              .filter((phase) => phase.id !== UNASSIGNED_PHASE_ID)
              .map((phase) => (
                <SelectItem key={phase.id} value={phase.id}>
                  {phase.name}
                </SelectItem>
              ))}
            {/* No "Unassigned" option. It was the third way to recreate the
                holding pen — the palette and the canvas drop were fixed, and
                this one, being pre-existing, was missed. A step can still be
                *shown* as unassigned (the trigger falls back to that label for
                a draft loaded before `fromWorkflowView` re-homes it), but it
                can only ever be moved out of the pen, never into it. */}
          </SelectContent>
        </Select>
      </div>

      <CommitField
        label="Prompt"
        value={step.prompt}
        multiline
        rows={6}
        // "Is an AI step", not "is not a gate" — the same way V-15 is written
        // in the service, and for the service's stated reason: a step type
        // added later has to opt in to the rule rather than escape it in
        // silence. Phrased as the negation, a third type would arrive here
        // already marked required while the rule that justifies the mark did
        // not reach it, and the panel would demand a prompt publication does
        // not want.
        required={step.type === 'ai_step'}
        field="prompt"
        onCommit={(prompt) => {
          edit(setStepField(stepId, 'prompt', prompt));
          resolvePrompt();
        }}
        // An AI step with an empty prompt has nothing to execute, and the
        // service refuses to publish it (V-15). A Human Gate has something to do
        // either way — wait for a person — so the rule does not reach it, and
        // telling its author otherwise would send them off to write prose
        // nothing requires.
        //
        // Keyed on "is an AI step" to match the mark above. The two are one
        // claim said twice — the asterisk that it is required, the hint when —
        // so a step type that escapes one must escape the other, or the panel
        // contradicts itself.
        hint={
          step.type === 'ai_step'
            ? 'What this step should do. Required before the workflow can be published.'
            : 'What the reviewer should be shown. Optional.'
        }
      />

      <LlmProfilePicker
        workspaceId={workspaceId}
        value={step.llm}
        needed={step.type === 'ai_step'}
        onCommit={(llm) => edit(setStepField(stepId, 'llm', llm))}
      />

      {/*
        Execution image (spec 091). Meaningful only for an AI step — a human
        gate never executes, so it has nothing to run the image against — the
        same rule `mode`/`assigneeUserId` follow in reverse just above.
      */}
      {step.type === 'ai_step' && (
        <div className="flex flex-col gap-1.5">
          <ImagePicker
            scope="step"
            workspaceId={workspaceId}
            value={step.image}
            onCommit={(image) => edit(setStepField(stepId, 'image', image))}
          />
          <ImageSourceIndicator draft={draft} step={step} />
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <CommitField
          label="Identifier"
          value={step.id}
          mono
          readOnly={identifierLocked}
          onCommit={(next) => {
            const candidate = slugify(next);
            if (candidate === step.id) return;
            if (idErrorFor(draft, candidate, step.id)) return;
            edit(setStepId(step.id, candidate));
            // The selection names the old id, so it would go stale the moment
            // the rename lands.
            onSelect({ kind: 'step', id: candidate });
          }}
          hint={
            identifierLocked
              ? 'Fixed once published: tasks reference this, and changing it would orphan them.'
              : 'Follows the name until you set it yourself.'
          }
        />
        {idError && (
          <p role="alert" className="text-destructive text-xs">
            {idError}
          </p>
        )}
      </div>

      <section className="flex flex-col gap-2">
        <h3 className="text-foreground text-sm font-medium">
          Outgoing transitions
        </h3>

        {/*
          The second door to `connect`, which is also what a canvas drag calls
          — so key derivation, the Done target and the guards are the same
          either way. It exists because the drag was the only way to declare a
          way out, and a gesture is not something a keyboard user can perform
          or a new user can discover.

          Deliberately not filtered by what is already connected for an AI
          step or Human Gate: the service is what refuses branching and loops
          there, and it reports them as publication problems. Second-guessing
          that here would mean two statements of one rule.
        */}
        <div className="flex flex-col gap-1.5">
          <Label>Add transition</Label>
          <Select
            value={pendingTarget}
            onValueChange={(next) => {
              if (!next) return;
              edit(connect(stepId, next));
              // Straight back to the placeholder: this control performs an
              // action, it does not hold a value.
              setPendingTarget('');
            }}
          >
            <SelectTrigger aria-label="Add transition">
              <SelectValue>
                {(value: string) =>
                  targets.find((target) => target.id === value)?.name ??
                  'Choose a target'
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {targets.map((target) => (
                <SelectItem key={target.id} value={target.id}>
                  {target.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {step.transitions.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            None yet. Choose a target above, or drag from this step&apos;s
            right-hand handle to another step.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {step.transitions.map((transition) => (
              <li
                key={transition.key}
                className="border-border flex items-center gap-2 rounded-md border p-2"
              >
                <button
                  type="button"
                  className="focus-visible:ring-ring min-w-0 flex-1 rounded-sm text-left focus-visible:ring-3 focus-visible:outline-none"
                  onClick={() =>
                    onSelect({
                      kind: 'transition',
                      stepId,
                      key: transition.key,
                    })
                  }
                >
                  <span className="text-foreground block truncate text-xs font-medium">
                    → {transition.to}
                  </span>
                  <span className="text-muted-foreground block truncate text-xs">
                    {transition.when || 'No condition yet'}
                  </span>
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove transition ${transition.key}`}
                  onClick={() => edit(deleteTransition(stepId, transition.key))}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** What the service accepts for a linked step's scheduling instructions (spec 183). */
const SCHEDULING_INSTRUCTIONS_MAX = 4000;

/** The words for a linked step's start choice (spec 140 US4). */
const START_CHILDREN_LABELS: Record<string, string> = {
  auto: 'Automatically',
  manual: 'Wait for a person',
};

const STEP_TYPE_LABELS: Record<string, string> = {
  ai_step: 'AI Step',
  human_gate: 'Human Gate',
  linked_workflow: 'Sub workflow',
};

const GATE_MODE_LABELS: Record<string, string> = {
  always_human: 'Always a person',
  ai_or_human: 'A person or an agent',
};

/**
 * A picker over the organization's existing LLM profiles (spec 080 — org-wide,
 * not workspace-scoped), replacing free-text entry of a slug (067 US2) — a
 * mistyped or stale identifier can no longer be saved, since only a fetched
 * profile's own slug is ever offered. This read stays open to any signed-in
 * person regardless of the organization-admin role (spec 080 US2) — only
 * managing profiles is admin-gated, not selecting one.
 *
 * Three states beyond the ordinary picker, in priority order:
 *
 * 1. **No profiles at all** (FR-008) — a functional-looking but empty picker
 *    would look broken rather than explain what to do next, so this renders
 *    no `Select` at all, only a link to create one — and only for an
 *    organization admin (spec 080 FR-012); anyone else would follow a link
 *    into a page that immediately refuses them.
 * 2. **Assigned a profile the fetched list does not contain** (FR-009) — the
 *    profile was deleted after this step was last saved. Shown as a named
 *    alert, not blank and not the raw slug standing in for a name it no
 *    longer has. (A step's reference cleared by the org-scoping migration's
 *    no-backfill reset lands in state 1's `value === null` case instead —
 *    that migration nulled the reference rather than leaving it dangling, so
 *    it is not this state.)
 * 3. **The ordinary case** — a `Select` naming every profile by name, value
 *    is the slug underneath.
 */
/**
 * Which level this step's effective execution image actually comes from
 * (spec 091 US3). Derived on every render, not fetched — `draft` already
 * holds both levels, so there is nothing to load.
 */
function ImageSourceIndicator({
  draft,
  step,
}: {
  draft: { image: string | null };
  step: { image: string | null };
}) {
  const resolved = resolveEffectiveImageSource(draft, step);
  const text =
    resolved.source === 'step'
      ? "This step's own image."
      : resolved.source === 'workflow'
        ? "Inherited from the workflow's image."
        : 'Inherited from the deployment default.';

  return <p className="text-muted-foreground text-xs">{text}</p>;
}

function LlmProfilePicker({
  workspaceId,
  value,
  needed,
  onCommit,
}: {
  workspaceId: string;
  value: string | null;
  /** An AI step, which publishing will refuse without a profile. */
  needed: boolean;
  onCommit: (slug: string) => void;
}) {
  const { entries, isPending, loadFailed } = useLlmProfiles();
  const isOrgAdmin = useIsOrgAdmin();

  if (isPending) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label>LLM profile</Label>
        <Select value="" disabled>
          <SelectTrigger aria-label="LLM profile">
            <SelectValue>{() => 'Loading…'}</SelectValue>
          </SelectTrigger>
          <SelectContent />
        </Select>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label>LLM profile</Label>
        <p role="alert" className="text-destructive text-xs">
          The organization&apos;s LLM profiles could not be loaded. Reload to
          try again.
        </p>
      </div>
    );
  }

  const profiles = entries ?? [];

  if (profiles.length === 0) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label>LLM profile</Label>
        <p className="text-muted-foreground text-xs">
          {isOrgAdmin ? (
            <>
              No LLM profiles exist yet.{' '}
              <Link
                to="/admin/ai-providers"
                className="text-foreground underline"
              >
                Create one
              </Link>{' '}
              before this step can be assigned a model.
            </>
          ) : (
            <>
              No LLM profiles exist yet. Ask an organization admin to create one
              before this step can be assigned a model.
            </>
          )}
        </p>
        {needed && <NeedsProfileHint />}
      </div>
    );
  }

  const nameFor = (slug: string): string | undefined =>
    profiles.find((profile: LlmProfile) => profile.slug === slug)?.name;
  const isBroken = value != null && nameFor(value) === undefined;
  const chosen = profiles.find((profile: LlmProfile) => profile.slug === value);

  return (
    <div className="flex flex-col gap-1.5">
      <Label>LLM profile</Label>
      <Select
        value={value ?? ''}
        onValueChange={(next) => {
          if (next) onCommit(next);
        }}
      >
        <SelectTrigger aria-label="LLM profile">
          <SelectValue>
            {(slug: string) => {
              if (!slug) return 'Choose a profile';
              // Never the raw slug (FR-009) — a name it no longer has is not
              // shown standing in for the name it used to have.
              return nameFor(slug) ?? 'Unknown profile';
            }}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {/* What each one runs on, so it is chosen knowingly (spec 214). */}
          {profiles.map((profile: LlmProfile) => (
            <SelectItem key={profile.slug} value={profile.slug}>
              <ProfileSummary profile={profile} variant="option" />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* The field stays one line; what the step runs on is said here, for
          whoever edits it next (spec 214 US2). */}
      {chosen && (
        <>
          <ProfileSummary profile={chosen} variant="card" />
          <AiModelsLink workspaceId={workspaceId} />
        </>
      )}
      {isBroken && (
        <p role="alert" className="text-destructive text-xs">
          The profile this step was assigned no longer exists.
        </p>
      )}
      {needed && (value === null || isBroken) && <NeedsProfileHint />}
    </div>
  );
}

/**
 * The step saves without a profile (spec 144 FR-007); this is what says it
 * will not publish without one. Not an alert: nothing went wrong, and an
 * author who adds a step and saves before choosing a model is doing exactly
 * what the draft is for.
 */
function NeedsProfileHint() {
  return (
    <p className="text-warning text-xs">
      Needs an AI profile before publishing
    </p>
  );
}
